import { useState, useEffect, useMemo, useCallback } from 'react';

// Stable at module load: window.marketingUrl is server-injected before React boots
const MARKETING_BASE_URL = window.marketingUrl || 'https://d2jth2langi9vb.cloudfront.net/marketing-content/production';

/**
 * Manages marketing content slots for a page rendered via sandboxed <iframe>s.
 *
 * Handles mobile detection, URL generation, and per-slot error state.
 * Because iframes do not reliably fire onError for HTTP status failures, this
 * hook prechecks each slot URL with a HEAD request on mount and marks the slot
 * as errored when the server returns a non-OK status. The returned setSlotError
 * is a secondary escape hatch for callers to signal embed-level failures, but
 * the HEAD precheck is the primary error-detection mechanism.
 *
 * @param {string} contentType - Page identifier used in URL and element ID
 *   naming (e.g. 'login', 'register'). Produces URLs of the form
 *   <marketingBaseUrl>/<contentType>-<slot>.html.
 * @param {string[]} [slots] - Marketing slots to load. Defaults to
 *   ['header', 'footer', 'background'].
 * @returns {{ mobile, urls, errors, headerId, footerId, setSlotError }}
 */
export default function useMarketingContent(contentType, slots = ['header', 'footer', 'background']) {
  const [mobile, setMobile] = useState(window.innerWidth <= 768);
  const [errors, setErrors] = useState(() =>
    Object.fromEntries(slots.map(slot => [slot, false]))
  );

  const urls = useMemo(() =>
    Object.fromEntries(
      slots.map(slot => [slot, `${MARKETING_BASE_URL}/${contentType}-${slot}.html`])
    )
  , [contentType, slots.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Element IDs for header and footer are used by the postMessage height-
  // adjustment listener in index.html, which looks elements up by ID.
  const headerId = `${contentType}MarketingHeader`;
  const footerId = `${contentType}MarketingFooter`;

  useEffect(() => {
    // HEAD precheck: iframes don't fire onError for HTTP errors, so we detect
    // 4xx/5xx responses here and mark the slot as errored before rendering.

    // A rejected fetch (CORS, network failure) does not mean the slot is
    // missing — the iframe may still load fine. Only an explicit non-OK HTTP
    // response should hide the slot.
    slots.forEach(slot => {
      fetch(urls[slot], { method: 'HEAD' })
        .then(res => { if (!res.ok) setSlotError(slot); })
        .catch(() => {});
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleResize = () => setMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const setSlotError = useCallback(slot => {
    setErrors(prev => ({ ...prev, [slot]: true }));
  }, []);

  return { mobile, urls, errors, headerId, footerId, setSlotError };
}
